import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InstituteNavigation from '../../components/ui/InstituteNavigation';
import JitsiMeetRoom from '../../components/JitsiMeetRoom';
import { useAuth } from '../../contexts/AuthContext';
import { sessionService } from '../../services/sessionService';

const InstituteVideoSession = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams?.get('sessionId');

  const [sessionState, setSessionState] = useState('waiting');
  const [currentSession, setCurrentSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentUser = {
    id: user?.id || 'institute-user',
    name: userProfile?.name || 'Institute Representative',
    role: 'institute'
  };

  // Load current session
  useEffect(() => {
    if (!user) return;

    const loadSession = async () => {
      try {
        const sessions = await sessionService?.getSessions(user?.id, 'institute');
        const activeSession = sessionId
          ? sessions?.find((s) => s?.id === parseInt(sessionId))
          : sessions?.find((s) => s?.status === 'ongoing' || s?.status === 'scheduled');

        if (activeSession) {
          setCurrentSession(activeSession);
          if (activeSession?.status === 'ongoing') {
            setSessionState('active');
          }
        }
      } catch (error) {
        console.error('Failed to load session:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSession();
  }, [user, sessionId]);

  const sessionInfo = {
    title: currentSession?.topic || 'Video Counseling Session',
    instituteName: userProfile?.name || 'Institute',
    studentName: currentSession?.studentName || 'Student',
    scheduledTime: currentSession?.time || 'Now',
  };

  // Same room name formula as student side — both join the same Jitsi room
  const jitsiRoomName = `StudyExpo-Session-${currentSession?.id || sessionId || 'demo'}`;

  const handleJoinSession = () => {
    setSessionState('active');
    if (currentSession) {
      sessionService?.updateSessionStatus(currentSession?.id, 'ongoing')?.catch(console.error);
    }
  };

  const handleLeaveSession = async () => {
    if (window.confirm('Are you sure you want to leave this session?')) {
      navigate('/institute-dashboard#sessions');
    }
  };

  const handleEndSession = async () => {
    if (window.confirm('Are you sure you want to end this session for all participants?')) {
      if (currentSession) {
        try {
          await sessionService?.updateSessionStatus(currentSession?.id, 'completed');
        } catch (err) {
          console.error('Error ending session:', err);
        }
      }
      navigate('/institute-dashboard#sessions');
    }
  };

  const handleCancelSession = () => {
    navigate('/institute-dashboard#sessions');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <InstituteNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading session...</p>
          </div>
        </div>
      </div>
    );
  }

  if (sessionState === 'waiting') {
    return (
      <div className="min-h-screen bg-background">
        <InstituteNavigation />
        <div className="h-[calc(100vh-64px)]">
          <JitsiMeetRoom
            roomName={jitsiRoomName}
            displayName={currentUser?.name}
            title={sessionInfo?.title}
            onClose={handleCancelSession}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <InstituteNavigation />
      <div className="flex flex-col" style={{ height: 'calc(100vh - 64px)' }}>
        {/* Session header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-2.5 h-2.5 bg-success rounded-full animate-pulse" />
            <div>
              <p className="text-white font-semibold text-sm">{sessionInfo?.title}</p>
              <p className="text-gray-400 text-xs">
                Student: {sessionInfo?.studentName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleLeaveSession}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs font-medium rounded-lg transition-colors"
            >
              Leave
            </button>
            <button
              onClick={handleEndSession}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-error hover:bg-error/80 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07C9.44 17.25 7.76 15.57 6.06 13.32a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/>
                <line x1="23" y1="1" x2="1" y2="23"/>
              </svg>
              End Session
            </button>
          </div>
        </div>

        {/* Jitsi Meet embedded room */}
        <div className="flex-1 min-h-0">
          <JitsiMeetRoom
            roomName={jitsiRoomName}
            displayName={currentUser?.name}
            title={sessionInfo?.title}
            onClose={handleEndSession}
          />
        </div>
      </div>
    </div>
  );
};

export default InstituteVideoSession;